import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Mail, Phone, MapPin, Clock, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success('Message sent successfully');
    } catch (err) {
      toast.error('Failed to send message');
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="contact-page">
      <section className="bg-[#1B2A4A] py-16 md:py-20">
        <div className="imcac-container">
          <p className="imcac-overline mb-3">Get In Touch</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight font-heading">Contact IMCAC</h1>
          <div className="h-1 w-20 bg-[#C5A572] mt-5" />
        </div>
      </section>

      <section className="imcac-section bg-white">
        <div className="imcac-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <h2 className="text-2xl font-semibold text-[#1B2A4A] font-heading mb-6">Contact Information</h2>
              <div className="space-y-5">
                {[
                  { icon: Mail, label: 'Email', value: 'admin@imcac.org', sub: 'General enquiries' },
                  { icon: Phone, label: 'Telephone', value: '+91 8382828215', sub: 'Mon-Fri, 9:00-17:00 IST' },
                  { icon: MapPin, label: 'Address', value: 'Unit 413, Swaroop Aditya Avenue, Marol Pipeline Rd, J B Nagar, Andheri East', sub: 'Mumbai, Maharashtra 400099, India' },
                  { icon: Clock, label: 'Office Hours', value: 'Monday - Friday', sub: '09:00 - 17:00 IST' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#F1F5F9] rounded-sm flex items-center justify-center flex-shrink-0">
                      <item.icon className="h-5 w-5 text-[#C5A572]" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[#1B2A4A]">{item.label}</p>
                      <p className="text-sm text-slate-700">{item.value}</p>
                      <p className="text-xs text-slate-500">{item.sub}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-7">
              {sent ? (
                <Card className="imcac-card" data-testid="contact-success">
                  <CardContent className="p-8 text-center">
                    <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-[#1B2A4A] font-heading mb-2">Message Sent</h2>
                    <p className="text-sm text-slate-600">Thank you for your enquiry. We will respond within 2-3 business days.</p>
                    <Button onClick={() => { setSent(false); setForm({ name: '', email: '', subject: '', message: '' }); }} className="mt-4 bg-[#1B2A4A] text-white rounded-sm" data-testid="send-another-btn">Send Another Message</Button>
                  </CardContent>
                </Card>
              ) : (
                <Card className="imcac-card" data-testid="contact-form-card">
                  <CardContent className="p-6 md:p-8">
                    <h2 className="text-xl font-semibold text-[#1B2A4A] font-heading mb-4">Send a Message</h2>
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm">Full Name</Label>
                          <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required placeholder="Your full name" className="rounded-sm mt-1" data-testid="contact-name" />
                        </div>
                        <div>
                          <Label className="text-sm">Email Address</Label>
                          <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required placeholder="your.email@example.com" className="rounded-sm mt-1" data-testid="contact-email" />
                        </div>
                      </div>
                      <div>
                        <Label className="text-sm">Subject</Label>
                        <Input value={form.subject} onChange={e => setForm({...form, subject: e.target.value})} required placeholder="Enquiry subject" className="rounded-sm mt-1" data-testid="contact-subject" />
                      </div>
                      <div>
                        <Label className="text-sm">Message</Label>
                        <Textarea value={form.message} onChange={e => setForm({...form, message: e.target.value})} required placeholder="Your message..." rows={5} className="rounded-sm mt-1 resize-none" data-testid="contact-message" />
                      </div>
                      <Button type="submit" disabled={loading} className="w-full bg-[#1B2A4A] hover:bg-[#152039] text-white rounded-sm" data-testid="contact-submit-btn">
                        {loading ? 'Sending...' : 'Send Message'}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
